/**
 * The small pieces every editor is built from: A2's `SheetActions`, `Helper`,
 * `FieldLabel` and the info line (profile/controls.tsx:117-395,
 * editors.tsx:107). Presentation only.
 */
import {
  "profile-editors.cancel" as cancelText,
  "profile-editors.loading" as loadingText,
  "profile-editors.save" as saveText,
} from "@repo/i18n";
import { Info } from "lucide-react";
import type { ReactNode } from "react";
import { cx } from "../../../lib/cx";
import { Button, Icon } from "../../../ui/controls";

export interface SheetActionsProps {
  cancelLabel?: string;
  danger?: boolean;
  disabled?: boolean;
  onCancel: () => void;
  onSave: () => void;
  pending?: boolean;
  saveLabel?: string;
}

/** Cancel, and the one action the sheet exists for. */
export function SheetActions({
  onCancel,
  onSave,
  saveLabel,
  cancelLabel,
  disabled,
  danger,
  pending,
}: SheetActionsProps) {
  return (
    <div className="flex gap-3">
      <Button
        className="bg-surface-2 shadow-none"
        onClick={onCancel}
        size="lg"
        variant="secondary"
      >
        {cancelLabel ?? cancelText()}
      </Button>
      <Button
        className="min-w-0 flex-1"
        disabled={(disabled ?? false) || (pending ?? false)}
        onClick={onSave}
        size="lg"
        variant={danger ? "danger" : "primary"}
      >
        {saveLabel ?? saveText()}
      </Button>
    </div>
  );
}

export function Helper({ children }: { children: ReactNode }) {
  return (
    <p className="mb-4 text-base text-ink-2 leading-relaxed">{children}</p>
  );
}

export function FieldLabel({
  children,
  meta,
  id,
}: {
  children: ReactNode;
  id?: string;
  meta?: ReactNode;
}) {
  return (
    <p
      className="mb-2.5 flex items-baseline justify-between gap-3 font-semibold text-base text-ink"
      id={id}
    >
      {children}
      {meta ? (
        <span className="font-medium text-ink-3 text-sm tabular-nums">
          {meta}
        </span>
      ) : null}
    </p>
  );
}

/** A2's quiet "why" line with the info glyph. `boxed` is the pain disclaimer. */
export function InfoNote({
  children,
  boxed,
}: {
  boxed?: boolean;
  children: ReactNode;
}) {
  return (
    <p
      className={cx(
        "mt-5 flex gap-2 text-ink-2 text-sm leading-snug",
        boxed === true && "rounded-lg bg-surface-2 p-3.5"
      )}
    >
      <span className="mt-px shrink-0 text-ink-3">
        <Icon icon={Info} size={16} />
      </span>
      <span>{children}</span>
    </p>
  );
}

/** A field error, announced. */
export function FieldError({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 text-danger text-sm" role="alert">
      {children}
    </p>
  );
}

/** What the body shows while the section it edits is still loading. */
export function LoadingBody() {
  return (
    <div aria-busy="true" className="flex flex-col gap-2">
      <span className="sr-only">{loadingText()}</span>
      <span className="h-14 animate-pulse rounded-lg bg-surface-2" />
      <span className="h-14 animate-pulse rounded-lg bg-surface-2" />
      <span className="h-14 animate-pulse rounded-lg bg-surface-2" />
    </div>
  );
}
